import { useCallback, useEffect, useState } from 'react'
import { moveValueRange, nowMs } from '../utils/common'
import { usePacketMetadata } from './usePacketMetadata'
import { realtimeMetadata } from '../utils/packet-workers'

export type TimeWindow = [number, number]

export enum PlaybackMode {
  Play,
  Pause,
  Live
}

export const REFRESH_RATE = 42
export const REFRESH_MS = 1000 / REFRESH_RATE
export const MIN_TIME_WINDOW_SIZE = 1000
const DEFAULT_TIME_WINDOW_SIZE = 10000

type PlaybackState = {
  mode: PlaybackMode
  interval?: NodeJS.Timeout
  timeWindow?: TimeWindow
}

const defaultState: () => PlaybackState = () => ({
  mode: PlaybackMode.Pause,
  interval: undefined,
  timeWindow: undefined
})

let _playbackState = defaultState()

let _timeWindowListeners: (() => void)[] = []
let _playbackModeListeners: (() => void)[] = []

const _setTimeWindow = (timeWindow: TimeWindow | undefined) => {
  _playbackState.timeWindow = timeWindow
  _timeWindowListeners.forEach((l) => l())
}

const _setPlaybackMode = (playbackMode: PlaybackMode) => {
  _playbackState.mode = playbackMode
  _playbackModeListeners.forEach((l) => l())
}

const _setTimeWindowEnd = async (endTimeMs: number): Promise<boolean> => {
  let notAtEnd: boolean = true

  const timeWindow = _playbackState.timeWindow
  const packetWindow = realtimeMetadata.timeWindow

  if (timeWindow === undefined || packetWindow === undefined) {
    return notAtEnd
  }

  if (packetWindow[1] <= endTimeMs) {
    endTimeMs = packetWindow[1]
    notAtEnd = false
  }

  const delta = endTimeMs - timeWindow[1]

  _setTimeWindow([timeWindow[0] + delta, timeWindow[1] + delta])

  return notAtEnd
}

const _moveTimeWindow = async (delta: number) => {
  const timeWindow = _playbackState.timeWindow
  const packetWindow = realtimeMetadata.timeWindow
  if (packetWindow === undefined || timeWindow === undefined) {
    return
  }

  _setTimeWindow(moveValueRange(timeWindow, packetWindow, delta))
}

const _goToEnd = async () => {
  const latestWindow = realtimeMetadata.timeWindow
  if (latestWindow === undefined) {
    return
  }

  _setTimeWindowEnd(latestWindow[1])
}

const _clearPlaybackInterval = () => {
  clearInterval(_playbackState.interval)
  _playbackState.interval = undefined
}

const _pause = () => {
  _clearPlaybackInterval()
  _setPlaybackMode(PlaybackMode.Pause)
}

const _play = (): void => {
  _clearPlaybackInterval()
  const startTimeOffsetMs = _playbackState.timeWindow?.[1]
  if (startTimeOffsetMs === undefined) {
    return
  }

  _setPlaybackMode(PlaybackMode.Play)

  const startTimeMs = nowMs()
  _playbackState.interval = setInterval(async () => {
    const msElapsed = nowMs() - startTimeMs
    if (!(await _setTimeWindowEnd(startTimeOffsetMs + msElapsed))) {
      _pause()
    }
  }, REFRESH_MS)
}

const _live = () => {
  _clearPlaybackInterval()
  _setPlaybackMode(PlaybackMode.Live)

  _playbackState.interval = setInterval(() => _goToEnd(), REFRESH_MS)
}

export const clearPlaybackState = () => {
  _pause()

  _playbackState = defaultState()

  _timeWindowListeners.forEach((l) => l())
}

export const usePlayback = (): {
  playbackMode: PlaybackMode
  timeWindow: TimeWindow | undefined
  setTimeWindow: (timeWindow: TimeWindow | undefined) => void
  moveTimeWindow: (delta: number) => void
  play: () => void
  pause: () => void
  live: () => void
} => {
  const [timeWindow, setTimeWindow] = useState<TimeWindow | undefined>(_playbackState.timeWindow)
  const [mode, setMode] = useState<PlaybackMode>(_playbackState.mode)

  const packetWindow = usePacketMetadata().timeWindow

  useEffect(() => {
    if (packetWindow === undefined) {
      return
    }

    if (_playbackState.timeWindow === undefined) {
      _setTimeWindow([packetWindow[0], packetWindow[0] + DEFAULT_TIME_WINDOW_SIZE])
    }
  }, [packetWindow])

  const setGlobalTimeWindow = useCallback((timeWindow: TimeWindow | undefined) => {
    _setTimeWindow(timeWindow)
  }, [])

  const moveGlobalTimeWindow = useCallback((delta: number) => {
    _moveTimeWindow(delta)
  }, [])

  useEffect(() => {
    const handlePlaybackModeChange = () => {
      setMode(_playbackState.mode)
    }

    const handleTimeWindowChange = () => {
      setTimeWindow(_playbackState.timeWindow)
    }

    _playbackModeListeners.push(handlePlaybackModeChange)
    _timeWindowListeners.push(handleTimeWindowChange)

    return () => {
      let index = _playbackModeListeners.indexOf(handlePlaybackModeChange)
      if (index !== -1) {
        _playbackModeListeners.splice(index, 1)
      }

      index = _timeWindowListeners.indexOf(handleTimeWindowChange)
      if (index !== -1) {
        _timeWindowListeners.splice(index, 1)
      }
    }
  }, [setTimeWindow, setMode])

  return {
    playbackMode: mode,
    timeWindow,
    setTimeWindow: setGlobalTimeWindow,
    moveTimeWindow: moveGlobalTimeWindow,
    play: _play,
    pause: _pause,
    live: _live
  }
}
